import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Flex, useToast, Image } from "@chakra-ui/react";
import LoginPage from "../Components/Authentication/LoginPage";
import SignupPage from "../Components/Authentication/SignupPage";
import OTPValidationPage from "../Components/Authentication/OTPValidationPage";
import {
    handlePhoneNumberCheck,
    handleEmailCheck,
    initiateSignUp,
    verifyOTP,
    initiateSignIn,
    setPendingAuth,
    setPendingChannel,
} from "../Services/aws/UserAuthenticationApiGateway";
import { useCustomerAuth } from "../Context/AuthContext";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LaundryContext } from "../Components/Contexts/LaundryContext";
import axios from "axios";
import { track } from "../utils/analytics";
import { bookingDestination, isPublicHomePath } from "../utils/bookingRedirect";

const apiError = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    return typeof detail === "string" ? detail : fallback;
};

const SHARED_EMAIL_MESSAGE = "This email is on more than one account, so it can't be used to sign in. Please use your phone number.";

export default function AuthenticationPage() {
    const { laundryId } = useParams();
    const { laundryData } = useContext(LaundryContext);
    const [currentPage, setCurrentPage] = useState("login");
    const [phoneNumber, setPhoneNumber] = useState("");
    const toast = useToast();
    const navigate = useNavigate();
    const { authStatus, onOTPVerified } = useCustomerAuth();
    const [isLoginLoading, setIsLoginLoading] = useState(false);
    const [isOTPLoading, setIsOTPLoading] = useState(false);
    const [isSignUpLoading, setIsSignUpLoading] = useState(false);
    const [customerFirstName, setCustomerFirstName] = useState("");
    const [searchParams] = useSearchParams();
    // Persist redirectTo so it survives the multi-step signup flow (login →
    // signup → OTP), where an intermediate navigation/reload can drop the query
    // string. Existing customers verify in one hop so they kept it; new
    // customers lost it and fell back to the default /user (schedule-order).
    const redirectTo = searchParams.get('redirectTo') || localStorage.getItem('pendingRedirect') || '';
    useEffect(() => {
        const rt = searchParams.get('redirectTo');
        if (!rt) return;
        if (isPublicHomePath(rt, laundryId)) localStorage.removeItem('pendingRedirect');
        else localStorage.setItem('pendingRedirect', rt);
    }, [searchParams, laundryId]);
    const refCode = searchParams.get('ref') || localStorage.getItem('pendingReferralCode') || '';
    const [otpTimer, setOtpTimer] = useState(180);
    const [commercialData, setCommercialData] = useState(null);
    const [referralCode, setReferralCode] = useState(refCode);

    const [loginEmail, setLoginEmail] = useState("");
    const [otpChannel, setOtpChannel] = useState("sms");
    const [otpSentTo, setOtpSentTo] = useState("");
    const [isSwitching, setIsSwitching] = useState(false);
    const isNewSignupRef = useRef(false);

    const handleLoginSubmit = async ({ phone, email }) => {
        setIsLoginLoading(true);
        try {
            if (email) {
                setLoginEmail(email);
                setPhoneNumber("");
                const response = await handleEmailCheck(email, laundryId);
                if (response.shared) {
                    toast({ title: "Use your phone number", description: SHARED_EMAIL_MESSAGE, status: "warning", duration: 7000, isClosable: true });
                } else if (response.exists) {
                    setCustomerFirstName(response.firstName);
                    setPendingAuth(null, laundryId, email, "email");
                    const otpResponse = await initiateSignIn(null, laundryId, { email });
                    setOtpChannel("email");
                    setOtpSentTo(otpResponse.sentTo || email);
                    setCurrentPage("otpValidation");
                } else {
                    setCurrentPage("signup");
                }
                return;
            }
            setPhoneNumber(phone);
            setLoginEmail("");
            const response = await handlePhoneNumberCheck(phone, laundryId);
            if (response.exists) {
                setCustomerFirstName(response.customerFirstName);
                setPendingAuth(phone, laundryId);
                const otpResponse = await initiateSignIn(phone, laundryId);
                if (!otpResponse.isSignedIn) {
                    setOtpChannel("sms");
                    setOtpSentTo(phone);
                    setCurrentPage("otpValidation");
                }
            } else {
                setCurrentPage("signup");
            }
        } catch (error) {
            toast({ title: "Error", description: apiError(error, "Something went wrong. Please try again."), status: "error", duration: 5000, isClosable: true });
        } finally {
            setIsLoginLoading(false);
        }
    };

    const handleSwitchToEmail = async () => {
        setIsSwitching(true);
        try {
            const otpResponse = await initiateSignIn(phoneNumber, laundryId, { channel: "email" });
            setPendingChannel("email");
            setOtpChannel("email");
            setOtpSentTo(otpResponse.sentTo || "your email");
            setOtpTimer(180);
            toast({ title: "Code emailed", status: "success", duration: 3000, isClosable: true });
        } catch (error) {
            toast({ title: "Couldn't email the code", description: apiError(error, "Please use the texted code."), status: "error", duration: 6000, isClosable: true });
        } finally {
            setIsSwitching(false);
        }
    };

    const handleOTPSubmit = async (otp) => {
        setIsOTPLoading(true);
        try {
            const result = await verifyOTP(otp);
            if (result.isSignedIn) {
                track(isNewSignupRef.current ? 'sign_up' : 'login', { method: otpChannel });
                isNewSignupRef.current = false;
                onOTPVerified(result);
                // Save commercial account settings if user signed up as commercial
                if (commercialData && result.user?.sub) {
                    try {
                        const API_URL = process.env.REACT_APP_AWS_API_URL || '';
                        const commercialPayload = {
                            customerId: result.user.sub,
                            laundryId: laundryId,
                        };
                        // Only assert commercial + billing email when the customer
                        // actually declared it, so a multi-property-only signup
                        // doesn't get flagged commercial.
                        if (commercialData.isCommercial) {
                            commercialPayload.isCommercial = true;
                            commercialPayload.billingEmail = commercialData.billingEmail;
                        }
                        if (commercialData.isMultiProperty) {
                            commercialPayload.isMultiProperty = true;
                        }
                        await axios.patch(`${API_URL}/api/admin/customer-commercial`, commercialPayload, {
                            headers: { Authorization: `Bearer ${result.accessToken}` }
                        });
                    } catch (commercialError) {
                        console.error("Failed to save commercial settings:", commercialError);
                        // Don't block signup - commercial settings can be updated later
                    }
                    setCommercialData(null);
                }
                toast({ title: "Verified!", status: "success", duration: 2000, isClosable: true });
            } else if (result.nextStep?.additionalInfo) {
                const remaining = result.nextStep.additionalInfo.attemptsLeft || 0;
                if (remaining > 0) {
                    toast({ title: `Wrong code. ${remaining} tries left.`, status: "warning", duration: 3000, isClosable: true });
                } else {
                    toast({ title: "Too many attempts. Try again.", status: "error", duration: 4000, isClosable: true });
                    setCurrentPage("login");
                }
            }
        } catch (error) {
            toast({ title: "Verification failed", description: error.message, status: "error", duration: 4000, isClosable: true });
        } finally {
            setIsOTPLoading(false);
        }
    };

    const handleSignupSubmit = async (phone, firstName, lastName, email, receivePhoneNotification, isCommercial, billingEmail, refCodeFromForm, isMultiProperty, receiveMarketingTexts) => {
        setIsSignUpLoading(true);
        // Store referral code for passing in the API call
        if (refCodeFromForm) setReferralCode(refCodeFromForm);
        try {
            const { error } = await initiateSignUp(laundryId, email, phone, firstName, lastName, false, receivePhoneNotification, isCommercial, billingEmail, refCodeFromForm || referralCode || null, receiveMarketingTexts);
            // Whether the account already existed. A brand-new register ALREADY
            // sent an OTP server-side, so sending a second one here would just
            // trip the per-phone 15s cooldown (429). Only an existing account
            // (register short-circuits with 409 before sending) needs us to
            // trigger the OTP send.
            let accountExisted = false;
            if (error) {
                if (error.includes("EMAIL_SHARED")) {
                    toast({ title: "Please add your phone number", description: "This email is already on more than one account. Add your phone number to sign up.", status: "warning", duration: 7000, isClosable: true });
                    return;
                }
                if (error.includes("UsernameExistsException")) {
                    accountExisted = true;
                    toast({ title: "Account exists", description: "Sending verification code...", status: "info", duration: 3000, isClosable: true });
                } else {
                    toast({ title: "Error", description: error, status: "error", duration: 4000, isClosable: true });
                    return;
                }
            }
            isNewSignupRef.current = !accountExisted;
            // Store commercial + multi-property data for after OTP verification.
            // Either flag alone is enough to trigger the post-OTP PATCH.
            if ((isCommercial && billingEmail) || isMultiProperty) {
                setCommercialData({
                    isCommercial: !!(isCommercial && billingEmail),
                    billingEmail: billingEmail || null,
                    isMultiProperty: !!isMultiProperty,
                });
            } else {
                setCommercialData(null);
            }
            // No phone: the code goes to the email instead.
            const channel = phone ? "sms" : "email";
            setPendingAuth(phone || null, laundryId, phone ? null : email, channel);
            // New account → register already sent the code; go straight to OTP.
            // Existing account → send the code now.
            if (accountExisted) {
                const otpResponse = await initiateSignIn(phone || null, laundryId, phone ? {} : { email });
                if (otpResponse.isSignedIn) {
                    setIsSignUpLoading(false);
                    return;
                }
            }
            setOtpChannel(channel);
            setOtpSentTo(phone || email);
            setPhoneNumber(phone);
            setCustomerFirstName(firstName);
            setCurrentPage("otpValidation");
        } catch (error) {
            toast({ title: "Error", description: apiError(error, error.message), status: "error", duration: 4000, isClosable: true });
        } finally {
            setIsSignUpLoading(false);
        }
    };

    useEffect(() => {
        if (authStatus === "authenticated") {
            const dest = bookingDestination(redirectTo, laundryId, `/${laundryId}/user`);
            localStorage.removeItem('pendingRedirect'); // consume once
            navigate(dest);
        }
    }, [authStatus, navigate, laundryId, redirectTo]);

    useEffect(() => {
        if (currentPage === "otpValidation") setOtpTimer(180);
    }, [currentPage]);

    const loginBg = (() => {
        const bgMap = {
            blue: "linear-gradient(180deg, #EBF8FF 0%, #BEE3F8 50%, #90CDF4 100%)",
            green: "linear-gradient(180deg, #F0FFF4 0%, #C6F6D5 50%, #9AE6B4 100%)",
            purple: "linear-gradient(180deg, #FAF5FF 0%, #D6BCFA 50%, #B794F4 100%)",
            teal: "linear-gradient(180deg, #E6FFFA 0%, #81E6D9 50%, #4FD1C5 100%)",
            orange: "linear-gradient(180deg, #FFFAF0 0%, #FBD38D 50%, #F6AD55 100%)",
            red: "linear-gradient(180deg, #FFF5F5 0%, #FEB2B2 50%, #FC8181 100%)",
            pink: "linear-gradient(180deg, #FFF5F7 0%, #FBB6CE 50%, #F687B3 100%)",
            cyan: "linear-gradient(180deg, #EDFDFD 0%, #9DECF9 50%, #76E4F7 100%)",
        };
        return bgMap[laundryData?.themeColor] || bgMap.blue;
    })();

    return (
        <Flex
            minH="100vh"
            bg={loginBg}
            align="center"
            justify="center"
            p={4}
            direction="column"
        >
            {laundryData?.laundryLogo && (
                <Image
                    src={laundryData.laundryLogo}
                    alt={laundryData?.laundryName}
                    maxW="260px"
                    maxH="130px"
                    objectFit="contain"
                    mb={4}
                />
            )}
            <Box
                bg="white"
                borderRadius="2xl"
                boxShadow="xl"
                w="100%"
                maxW="440px"
                overflow="hidden"
            >
                {authStatus === "configuring" && (
                    <Flex h="200px" align="center" justify="center">
                        <Box className="spinner" />
                    </Flex>
                )}
                {authStatus === "unauthenticated" && currentPage === "login" && (
                    <LoginPage onLoginSubmit={handleLoginSubmit} isLoginLoading={isLoginLoading} initialPhoneNumber={phoneNumber} initialEmail={loginEmail} />
                )}
                {authStatus === "unauthenticated" && currentPage === "signup" && (
                    <SignupPage onSubmit={handleSignupSubmit} phoneNumber={phoneNumber} initialEmail={loginEmail} isSignUpLoading={isSignUpLoading} initialReferralCode={referralCode} laundryId={laundryId} laundryName={laundryData?.laundryName} allowSelfDeclareCommercial={!!laundryData?.allowCustomerSelfDeclareCommercial} />
                )}
                {authStatus === "unauthenticated" && currentPage === "otpValidation" && (
                    <OTPValidationPage
                        phoneNumber={otpSentTo || phoneNumber}
                        channel={otpChannel}
                        onSwitchToEmail={phoneNumber ? handleSwitchToEmail : undefined}
                        isSwitching={isSwitching}
                        onOTPSubmit={handleOTPSubmit}
                        isOTPLoading={isOTPLoading}
                        customerFirstName={customerFirstName}
                        otpTimer={otpTimer}
                        setOtpTimer={setOtpTimer}
                    />
                )}
            </Box>
        </Flex>
    );
}
